import React, { useState, useEffect } from "react";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import { leadsAPI } from "../../services/api";
import "./Admin.css";

const TYPE_LABELS = {
  loan: "Prestamo",
  card: "Tarjeta",
  contact: "Contacto",
};

const TYPE_COLORS = {
  loan: "#667eea",
  card: "#764ba2",
  contact: "#3498db",
};

const STATUS_LABELS = {
  new: "Nuevo",
  contacted: "Contactado",
  qualified: "Calificado",
  rejected: "Rechazado",
  archived: "Archivado",
};

const STATUS_COLORS = {
  new: "#2ecc71",
  contacted: "#f39c12",
  qualified: "#3498db",
  rejected: "#e74c3c",
  archived: "#95a5a6",
};

const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    fetchLeads();
  }, [typeFilter, statusFilter, page]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (typeFilter) params.append("type", typeFilter);
      if (statusFilter) params.append("status", statusFilter);
      params.append("page", page);
      params.append("limit", 20);

      const res = await leadsAPI.getLeads(params.toString());
      setLeads(res.data.leads);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await leadsAPI.updateLead(leadId, { status: newStatus });
      fetchLeads();
    } catch (error) {
      console.error("Error updating lead:", error);
    }
  };

  const handleDelete = async (leadId) => {
    if (window.confirm("¿Está seguro de eliminar este lead?")) {
      try {
        await leadsAPI.deleteLead(leadId);
        fetchLeads();
      } catch (error) {
        console.error("Error deleting lead:", error);
      }
    }
  };

  const getDetail = (lead) => {
    if (lead.type === "loan") {
      return `$${lead.loanAmount?.toLocaleString() || "N/A"} - ${lead.loanPurpose || ""}`;
    }
    if (lead.type === "card") {
      return `${lead.cardType || ""} - ${lead.bank || ""}`;
    }
    return lead.subject || "";
  };

  return (
    <DashboardLayout title="Gestion de Leads">
      {/* Filters */}
      <div className="filters-bar">
        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los tipos</option>
          {Object.entries(TYPE_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        <span className="results-count">{total} resultados</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading">Cargando...</div>
      ) : (
        <>
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Telefono</th>
                  <th>Detalle</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="empty-row">
                      No hay leads registrados
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead._id}>
                      <td>
                        <span
                          className="status-badge"
                          style={{ backgroundColor: TYPE_COLORS[lead.type] }}
                        >
                          {TYPE_LABELS[lead.type]}
                        </span>
                      </td>
                      <td>
                        {lead.firstName} {lead.lastName}
                      </td>
                      <td>{lead.email}</td>
                      <td>{lead.phone}</td>
                      <td>{getDetail(lead)}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{
                            backgroundColor: STATUS_COLORS[lead.status],
                          }}
                        >
                          {STATUS_LABELS[lead.status] || lead.status}
                        </span>
                      </td>
                      <td>
                        {new Date(lead.createdAt).toLocaleDateString()}
                      </td>
                      <td className="actions-cell">
                        <select
                          value={lead.status}
                          onChange={(e) =>
                            handleStatusChange(lead._id, e.target.value)
                          }
                          className="action-select"
                        >
                          {Object.entries(STATUS_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>
                              {label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleDelete(lead._id)}
                          className="btn-danger-sm"
                        >
                          Eliminar
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </button>
              <span>
                Pagina {page} de {totalPages}
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default Leads;
